import { computed, Injectable, signal, Signal, WritableSignal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class TypewriterService {
  private typewriterSignals = new Map<
    string,
    {
      text: WritableSignal<string>;
      isComplete: WritableSignal<boolean>;
      interval?: ReturnType<typeof setInterval>;
    }
  >();
  private readonly activeTypewriterCount = signal(0);
  public readonly hasActiveTypewriters = computed(() => this.activeTypewriterCount() > 0);

  /**
   * Creates a typewriter effect for a given text
   * @param text The text to animate
   * @param speed Speed in milliseconds between characters (default: 50ms, automatically reduced for texts > 300 chars)
   * @param id Unique identifier for this typewriter instance
   * @returns Object with text signal and completion status
   */
  typewrite(
    text: string,
    speed = 50,
    id = 'default',
  ): {
    text: Signal<string>;
    isComplete: Signal<boolean>;
  } {
    // Clean up existing typewriter if it exists
    this.stopTypewriter(id);

    // Automatically use fast speed (5ms) for long messages (> 360 characters)
    const autoAdjustedSpeed = text.length > 360 ? 5 : speed;

    const textSignal = signal('');
    const isCompleteSignal = signal(false);

    this.typewriterSignals.set(id, {
      text: textSignal,
      isComplete: isCompleteSignal,
    });
    this.activeTypewriterCount.update((count) => count + 1);

    let currentIndex = 0;

    const typeInterval = setInterval(() => {
      if (currentIndex <= text.length) {
        textSignal.set(text.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typeInterval);
        isCompleteSignal.set(true);
        this.activeTypewriterCount.update((count) => Math.max(0, count - 1));
        this.typewriterSignals.delete(id);
      }
    }, autoAdjustedSpeed);

    // Store the interval for cleanup
    const typewriterData = this.typewriterSignals.get(id);
    if (typewriterData) {
      typewriterData.interval = typeInterval;
    }

    return {
      text: textSignal.asReadonly(),
      isComplete: isCompleteSignal.asReadonly(),
    };
  }

  /**
   * Stops a typewriter animation
   * @param id The identifier of the typewriter to stop
   */
  stopTypewriter(id: string): void {
    const typewriterData = this.typewriterSignals.get(id);
    if (typewriterData) {
      if (typewriterData.interval) {
        clearInterval(typewriterData.interval);
      }
      this.activeTypewriterCount.update((count) => Math.max(0, count - 1));
      this.typewriterSignals.delete(id);
    }
  }

  /**
   * Clean up all typewriter instances
   */
  cleanup(): void {
    this.typewriterSignals.forEach((data) => {
      if (data.interval) {
        clearInterval(data.interval);
      }
    });
    this.typewriterSignals.clear();
    this.activeTypewriterCount.set(0);
  }
}
